import { useEffect, useState } from "react";
import { suppliersApi } from "../../services/suppliersApi";
import { useToast } from "../../context/ToastContext";
import { useFeatureTheme } from "../../context/FeatureThemeContext";
import { Card } from "../../components/common/Card";
import { PageHeader } from "../../components/common/PageHeader";
import { Button } from "../../components/common/Button";
import { DataTable } from "../../components/common/DataTable";
import { Modal } from "../../components/common/Modal";
import { LockedFeature } from "../../components/common/LockedFeature";

// The stored value is still 1-5 (app.Suppliers.rating), but a bare integer
// does not say whether high is good. The words are the point.
const RATINGS = [
  { value: 5, label: "Excellent" },
  { value: 4, label: "Good" },
  { value: 3, label: "Average" },
  { value: 2, label: "Poor" },
  { value: 1, label: "Avoid" },
];

export function SuppliersPage() {
  const { notify } = useToast();
  const { hasFeature } = useFeatureTheme();
  const enabled = hasFeature("EquipmentManagement");
  const [suppliers, setSuppliers] = useState(null);
  const [open, setOpen] = useState(false);
  /*
   * Phone, what they supply, delivery time and payment terms were reported
   * missing (#252) -- and app.Suppliers has carried all four since V009,
   * along with the request model and the list query. Nothing needed adding
   * anywhere but here: the form collected four fields out of eight and the
   * table showed the rest as blanks nobody could fill.
   *
   * The reason to keep a supplier record at all is to answer "who do I call
   * for foundation in March", which needs the phone number and what they
   * actually sell.
   */
  const [form, setForm] = useState({
    name: "", contact_name: "", contact_email: "", contact_phone: "",
    notes: "", payment_terms: "", delivery_time_days: "", rating: 5,
  });

  async function load() {
    const r = await suppliersApi.list();
    setSuppliers(r.suppliers);
  }
  /* Only ask for data the account can use. A locked page still renders (blurred
       preview), but its requests were refused by the server and logged as 403s in
       the console; `enabled` also re-runs this once the plan has loaded. */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (enabled) load(); }, [enabled]);

  async function submit() {
    try {
      await suppliersApi.create({
        name: form.name.trim(),
        contact_name: form.contact_name.trim() || undefined,
        contact_email: form.contact_email.trim() || undefined,
        contact_phone: form.contact_phone.trim() || undefined,
        notes: form.notes.trim() || undefined,
        payment_terms: form.payment_terms.trim() || undefined,
        delivery_time_days: form.delivery_time_days ? Number(form.delivery_time_days) : undefined,
        rating: Number(form.rating),
      });
      notify("Supplier added", "success");
      setOpen(false);
      setForm({
        name: "", contact_name: "", contact_email: "", contact_phone: "",
        notes: "", payment_terms: "", delivery_time_days: "", rating: 5,
      });
      load();
    } catch (err) {
      notify(err.apiMessage || "Could not add supplier", "danger");
    }
  }

  return (
    <div>
      <PageHeader title="Suppliers">
        {/* Inside the gate: POST /suppliers requires EquipmentManagement, so
            offering the button to everyone let a Basic user fill in the whole
            form and collect a 403 on save. Same shape as the archive bug. */}
        {hasFeature("EquipmentManagement") && <Button onClick={() => setOpen(true)}>Add Supplier</Button>}
      </PageHeader>
      <LockedFeature enabled={hasFeature("EquipmentManagement")} featureLabel="Equipment Management" previewText="Track your suppliers alongside equipment inventory, maintenance, and reorder alerts.">
        <Card>
          <DataTable
            rows={suppliers}
            loading={suppliers === null}
            rowKey="supplier_id"
            emptyTitle="No suppliers yet"
            emptyDescription="Where you buy equipment and feed, so a cost stays attached to a source."
            columns={[
              { key: "name", header: "Name" },
              { key: "notes", header: "Supplies" },
              {
                key: "contact_name",
                header: "Contact",
                // Name and number in one cell: they are one fact -- who to
                // call -- and splitting them made two half-empty columns.
                render: (r) => [r.contact_name, r.contact_phone].filter(Boolean).join(" · ") || "—",
              },
              { key: "rating", header: "Rating", render: (r) => "★".repeat(r.rating || 0) },
              { key: "delivery_time_days", header: "Delivery (days)" },
            ]}
          />
        </Card>
      </LockedFeature>
      <Modal open={open} onClose={() => setOpen(false)} title="Add Supplier" footer={<Button onClick={submit}>Save</Button>}>
        {/* Every field carries a real <label>, not a placeholder. A
            placeholder vanishes the moment you type, and on the rating it
            never appeared at all -- the field defaults to 5, so it was never
            empty, leaving a bare number picker with nothing saying what it
            was for. Reported from the field. */}
        <div style={{ display: "grid", gap: "var(--space-3)" }}>
          <label>
            Supplier name
            <input id="suppliers-page-name" name="name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              className="touch-target"
              style={{ display: "block", width: "100%" }}
            />
          </label>
          <label>
            Contact name <span style={{ color: "var(--color-text-muted)" }}>(optional)</span>
            <input id="suppliers-page-contact-name" name="contact-name"
              value={form.contact_name}
              onChange={(e) => setForm((f) => ({ ...f, contact_name: e.target.value }))}
              className="touch-target"
              style={{ display: "block", width: "100%" }}
            />
          </label>
          <label>
            Contact email <span style={{ color: "var(--color-text-muted)" }}>(optional)</span>
            <input id="suppliers-page-contact-email" name="contact-email"
              type="email"
              value={form.contact_email}
              onChange={(e) => setForm((f) => ({ ...f, contact_email: e.target.value }))}
              className="touch-target"
              style={{ display: "block", width: "100%" }}
            />
          </label>
          <label>
            Contact phone <span style={{ color: "var(--color-text-muted)" }}>(optional)</span>
            <input id="suppliers-page-contact-phone" name="contact-phone"
              type="tel"
              value={form.contact_phone}
              onChange={(e) => setForm((f) => ({ ...f, contact_phone: e.target.value }))}
              className="touch-target"
              style={{ display: "block", width: "100%" }}
            />
          </label>
          <label>
            What they supply <span style={{ color: "var(--color-text-muted)" }}>(optional)</span>
            <textarea id="suppliers-page-notes" name="notes"
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              rows={2}
              style={{ display: "block", width: "100%" }}
            />
            <span style={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-muted)" }}>
              Foundation, jars, woodenware — whatever you actually go to them for.
            </span>
          </label>
          <label>
            Payment terms <span style={{ color: "var(--color-text-muted)" }}>(optional)</span>
            <input id="suppliers-page-payment-terms" name="payment-terms"
              value={form.payment_terms}
              onChange={(e) => setForm((f) => ({ ...f, payment_terms: e.target.value }))}
              className="touch-target"
              style={{ display: "block", width: "100%" }}
            />
          </label>
          <label>
            Typical delivery time in days <span style={{ color: "var(--color-text-muted)" }}>(optional)</span>
            <input id="suppliers-page-delivery-time-days" name="delivery-time-days"
              type="number"
              min="0"
              value={form.delivery_time_days}
              onChange={(e) => setForm((f) => ({ ...f, delivery_time_days: e.target.value }))}
              className="touch-target"
              style={{ display: "block", width: "100%" }}
            />
          </label>
          <label>
            Rating
            {/* Words, not bare integers: "3" tells you nothing about whether
                high is good. Picking from named options also matches the
                house rule of choosing over typing. */}
            <select id="suppliers-page-rating" name="rating"
              value={form.rating}
              onChange={(e) => setForm((f) => ({ ...f, rating: e.target.value }))}
              className="touch-target"
              style={{ display: "block", width: "100%" }}
            >
              {RATINGS.map((r) => (
                <option key={r.value} value={r.value}>{r.value} — {r.label}</option>
              ))}
            </select>
            <span style={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-muted)" }}>
              How reliable this supplier has been. 5 is best.
            </span>
          </label>
        </div>
      </Modal>
    </div>
  );
}
